/**
 * Series Detail Page
 * View and manage a series (for creators and public viewing)
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSeriesStore } from '../stores/seriesStore';
import { getSmallThumbnailUrl } from '../utils/mediaUrl';
import './SeriesDetailPage.css';
import { BilingualTitle } from '../components/common/BilingualTitle';

const formatDuration = (seconds?: number): string => {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

interface Course {
  id: string;
  title: string;
  titles?: Record<string, string>;
  description?: string;
  coverImageUrl?: string;
  thumbnailUrl?: string;
  difficultyLevel?: string;
  cefrLevel?: string;
  durationSeconds?: number;
  orderInSeries?: number;
}

const SeriesDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['series', 'common']);
  const {
    selectedSeries: series,
    loading,
    error,
    fetchSeriesById,
    updateSeries,
    removeCourseFromSeries,
    reorderCourses,
    clearSelectedSeries,
  } = useSeriesStore();

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsPublished, setEditIsPublished] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      fetchSeriesById(id);
    }
    return () => {
      clearSelectedSeries();
    };
  }, [id, fetchSeriesById, clearSelectedSeries]);

  useEffect(() => {
    if (series) {
      setEditTitle(series.title);
      setEditDescription(series.description || '');
      setEditIsPublished(series.isPublished);
    }
  }, [series]);

  const handleSaveEdit = async () => {
    if (!id || !editTitle.trim()) return;

    try {
      await updateSeries(id, {
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
        isPublished: editIsPublished,
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update series:', err);
    }
  };

  const handleRemoveCourse = async (courseId: string) => {
    if (!id) return;
    if (window.confirm(t('series:detail.removeConfirm'))) {
      try {
        await removeCourseFromSeries(id, courseId);
      } catch (err) {
        console.error('Failed to remove course:', err);
      }
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (!id || draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const courses = series?.courses || [];
    const newCourses = [...courses];
    const [removed] = newCourses.splice(draggedIndex, 1);
    newCourses.splice(dropIndex, 0, removed);

    const courseIds = newCourses.map((c) => c.id);
    setDraggedIndex(null);

    try {
      await reorderCourses(id, courseIds);
    } catch (err) {
      console.error('Failed to reorder courses:', err);
    }
  };

  if (loading && !series) {
    return (
      <div className="series-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('series:detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="series-detail-page">
        <div className="error-container">
          <h2>{t('series:detail.error')}</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)}>{t('series:detail.goBack')}</button>
        </div>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="series-detail-page">
        <div className="error-container">
          <h2>{t('series:detail.notFound')}</h2>
          <button onClick={() => navigate(-1)}>{t('series:detail.goBack')}</button>
        </div>
      </div>
    );
  }

  const courses: Course[] = series.courses || [];

  return (
    <div className="series-detail-page">
      <div className="series-detail-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          {t('series:detail.back')}
        </button>

        {isEditing ? (
          <div className="edit-form">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder={t('series:detail.titlePlaceholder')}
              className="edit-title-input"
              maxLength={100}
            />
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder={t('series:detail.descriptionPlaceholder')}
              className="edit-description-input"
              rows={3}
              maxLength={500}
            />
            <label className="publish-toggle">
              <input
                type="checkbox"
                checked={editIsPublished}
                onChange={(e) => setEditIsPublished(e.target.checked)}
              />
              <span>{t('series:detail.publishedToggle')}</span>
            </label>
            <div className="edit-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setIsEditing(false);
                  setEditTitle(series.title);
                  setEditDescription(series.description || '');
                  setEditIsPublished(series.isPublished);
                }}
              >
                {t('common:cancel')}
              </button>
              <button
                className="btn-save"
                onClick={handleSaveEdit}
                disabled={!editTitle.trim()}
              >
                {t('common:save')}
              </button>
            </div>
          </div>
        ) : (
          <div className="header-content">
            <div className="header-info">
              <h1>{series.title}</h1>
              {series.description && (
                <p className="series-description">{series.description}</p>
              )}
              <div className="series-meta">
                <span className="course-count">
                  {courses.length === 1
                    ? t('series:courseCountSingular', { count: courses.length })
                    : t('series:courseCount', { count: courses.length })}
                </span>
                <span
                  className={`status-badge ${series.isPublished ? 'published' : 'draft'}`}
                >
                  {series.isPublished ? t('series:published') : t('series:draft')}
                </span>
              </div>
            </div>
            <button className="btn-edit" onClick={() => setIsEditing(true)}>
              {t('series:detail.editSeries')}
            </button>
          </div>
        )}
      </div>

      <div className="courses-section">
        <div className="section-header">
          <h2>{t('series:detail.coursesTitle')}</h2>
          <p className="drag-hint">{t('series:detail.dragHint')}</p>
        </div>

        {courses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <h3>{t('series:detail.emptyCourses')}</h3>
            <p>{t('series:detail.emptyCoursesDescription')}</p>
          </div>
        ) : (
          <div className="courses-list">
            {courses.map((course, index) => (
              <div
                key={course.id}
                className={`course-item ${draggedIndex === index ? 'dragging' : ''}`}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={() => setDraggedIndex(null)}
              >
                <div className="drag-handle">⋮⋮</div>
                <div className="course-order">{index + 1}</div>
                <div className="course-thumbnail">
                  {(course.coverImageUrl || course.thumbnailUrl) ? (
                    <img src={getSmallThumbnailUrl(course.coverImageUrl || course.thumbnailUrl)} alt="" />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <span>📖</span>
                    </div>
                  )}
                </div>
                <div className="course-info">
                  <Link to={`/courses/${course.id}`} className="course-title">
                    <BilingualTitle
                      title={course.title}
                      titles={course.titles}
                      variant="card"
                      as="span"
                    />
                  </Link>
                  {course.description && (
                    <p className="course-description">{course.description}</p>
                  )}
                  <div className="course-meta">
                    {(course.cefrLevel || course.difficultyLevel) && (
                      <span className="difficulty-badge">{course.cefrLevel || course.difficultyLevel}</span>
                    )}
                    {course.durationSeconds != null && course.durationSeconds >= 30 && (
                      <span className="duration">
                        {formatDuration(course.durationSeconds)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="btn-remove"
                  onClick={() => handleRemoveCourse(course.id)}
                  title={t('series:detail.removeFromSeries')}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesDetailPage;
